import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Chip, 
  Box, 
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  useTheme,
  Tooltip,
  alpha
} from '@mui/material';
import { 
  Person, 
  CalendarToday, 
  AccessTime,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  LocalHospital as LocalHospitalIcon,
  MedicalInformation as MedicalInformationIcon,
  Warning as WarningIcon,
  MedicalServices as MedicalServicesIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useAuth } from '../../auth/AuthContext';
import { claimProcedureRequest } from '../../services/healinApi';

const ProcedureRequestCard = ({ procedureRequest, onSelect, onClaim, onIssue }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [expandedProcedures, setExpandedProcedures] = useState(false);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPatientInfo, setExpandedPatientInfo] = useState(false);

  const handleExpandProcedures = () => {
    setExpandedProcedures(!expandedProcedures);
  };

  const handleOpenClaimDialog = () => {
    setClaimDialogOpen(true);
  };

  const handleCloseClaimDialog = () => {
    setClaimDialogOpen(false);
  };

  const handleOpenIssueDialog = () => {
    setIssueDialogOpen(true);
  };

  const handleCloseIssueDialog = () => {
    setIssueDialogOpen(false);
  };

  const handleClaim = async () => {
    if (!user || !user.username) {
      console.error('No authenticated user found');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const claimData = {
        userId: procedureRequest.userId,
        requestId: procedureRequest.requestId,
        assignedDoctorId: user.username
      };
      
      console.log('Claiming procedure request with data:', claimData);
      const result = await claimProcedureRequest(claimData);
      
      console.log('Procedure request claimed successfully:', result);
      setClaimDialogOpen(false);
      
      // Call the onClaim callback if provided (for parent component to refresh data)
      if (onClaim) {
        onClaim(procedureRequest.requestId);
      }
    } catch (error) {
      console.error('Error claiming procedure request:', error);
      // You might want to show a user-friendly error message here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIssue = () => {
    if (onIssue) {
      onIssue();
    }
    setIssueDialogOpen(false);
  };

  const togglePatientInfo = () => {
    setExpandedPatientInfo(!expandedPatientInfo);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Safely access nested properties
  const getPatientInfo = () => {
    return procedureRequest.patientInfo || {
      age: 'N/A',
      gender: 'N/A',
      bloodType: 'N/A',
      allergies: [],
      chronicConditions: []
    };
  };

  // Safely access procedures
  const getProcedures = () => {
    if (!procedureRequest.procedures || !Array.isArray(procedureRequest.procedures) || procedureRequest.procedures.length === 0) {
      return [];
    }
    return procedureRequest.procedures;
  };

  // Check if status is available (pending) for claiming
  const isAvailableStatus = (status) => {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'available' || lowerStatus === 'pending';
  };

  // Check if status is claimed for issuing
  const isClaimedStatus = (status) => {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'claimed';
  };

  // Check if status is issued
  const isIssuedStatus = (status) => {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'issued';
  };

  // Get status display info
  const getStatusInfo = () => {
    if (isIssuedStatus(procedureRequest.status)) {
      return { label: "Issued", color: "secondary" };
    } else if (isClaimedStatus(procedureRequest.status)) {
      return { label: "Claimed", color: "primary" };
    } else {
      return { label: "Available", color: "success" };
    }
  };

  // Get procedure type color based on class
  const getProcedureTypeColor = (procedureType) => {
    if (!procedureType) return { color: 'default', bgcolor: alpha(theme.palette.grey[500], 0.1) };
    
    const type = procedureType.toLowerCase();
    
    // Define color mapping for different procedure classes
    if (type.includes('laboratório') || type.includes('laboratorio') || type.includes('lab')) {
      return { 
        color: theme.palette.success.main, 
        bgcolor: alpha(theme.palette.success.main, 0.1),
        borderColor: alpha(theme.palette.success.main, 0.3)
      };
    } else if (type.includes('exames complementares') || type.includes('exame') || type.includes('imaging') || type.includes('radiology')) {
      return { 
        color: theme.palette.info.main, 
        bgcolor: alpha(theme.palette.info.main, 0.1),
        borderColor: alpha(theme.palette.info.main, 0.3)
      };
    } else if (type.includes('cirurgia') || type.includes('surgery') || type.includes('procedimento') || type.includes('procedure')) {
      return { 
        color: theme.palette.error.main, 
        bgcolor: alpha(theme.palette.error.main, 0.1),
        borderColor: alpha(theme.palette.error.main, 0.3)
      };
    } else if (type.includes('consulta') || type.includes('consultation') || type.includes('appointment')) {
      return { 
        color: theme.palette.warning.main, 
        bgcolor: alpha(theme.palette.warning.main, 0.1),
        borderColor: alpha(theme.palette.warning.main, 0.3)
      };
    } else if (type.includes('terapia') || type.includes('therapy') || type.includes('rehabilitation')) {
      return { 
        color: theme.palette.secondary.main, 
        bgcolor: alpha(theme.palette.secondary.main, 0.1),
        borderColor: alpha(theme.palette.secondary.main, 0.3)
      };
    } else {
      // Default color for unknown types
      return { 
        color: theme.palette.primary.main, 
        bgcolor: alpha(theme.palette.primary.main, 0.1),
        borderColor: alpha(theme.palette.primary.main, 0.3)
      };
    }
  };

  return (
    <>
      <Card 
        elevation={3} 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          },
          cursor: onSelect ? 'pointer' : 'default'
        }}
        onClick={onSelect ? () => onSelect() : undefined}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" component="div" gutterBottom noWrap>
              Procedure Request
            </Typography>
            <Chip 
              label={getStatusInfo().label} 
              color={getStatusInfo().color} 
              size="small" 
              sx={{ ml: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Patient: {procedureRequest.patientName}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarToday fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Requested: {formatDate(procedureRequest.requestedAt || procedureRequest.createdAt)}
            </Typography>
          </Box>
          
          {isIssuedStatus(procedureRequest.status) && procedureRequest.issuedAt && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Issued: {formatDate(procedureRequest.issuedAt)}
              </Typography>
            </Box>
          )}
          
          {procedureRequest.status === 'CLAIMED' && procedureRequest.assignedDoctorId && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Doctor ID: {procedureRequest.assignedDoctorId}
              </Typography>
            </Box>
          )}

          {/* Patient Information Section */}
          <Box 
            sx={{ 
              mt: 2, 
              p: 1, 
              borderRadius: 1, 
              bgcolor: alpha(theme.palette.info.main, 0.08)
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MedicalInformationIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main }} />
                <Typography variant="subtitle2" color="text.primary">
                  Patient Information
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  togglePatientInfo(); 
                }}
                sx={{ p: 0, cursor: 'pointer' }}
              >
                {expandedPatientInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={expandedPatientInfo} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1, ml: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                <Typography variant="body2">
                  Age: {getPatientInfo().age}, Gender: {getPatientInfo().gender}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <LocalHospitalIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                <Typography variant="body2">
                  Blood Type: {getPatientInfo().bloodType}
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>Chronic Conditions:</Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2 }}>
                {getPatientInfo().chronicConditions.map((condition, index) => (
                  <Typography component="li" variant="body2" key={index}>
                    {condition}
                  </Typography>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                <WarningIcon fontSize="small" sx={{ mr: 1, color: theme.palette.warning.main, fontSize: '0.9rem', mt: 0.3 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Allergies:</Typography>
                  {getPatientInfo().allergies.length > 0 ? (
                    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                      {getPatientInfo().allergies.map((allergy, index) => (
                        <Typography component="li" variant="body2" key={index} color="warning.main">
                          {allergy}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">No known allergies</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Collapse>

          {/* Procedures Section */}
          <Box 
            sx={{ 
              mt: 2, 
              p: 1, 
              borderRadius: 1, 
              bgcolor: alpha(theme.palette.primary.main, 0.08)
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MedicalServicesIcon fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" color="text.primary">
                  Procedures ({getProcedures().length})
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  handleExpandProcedures(); 
                }}
                sx={{ cursor: 'pointer' }}
              >
                {expandedProcedures ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={expandedProcedures} timeout="auto" unmountOnExit>
            {getProcedures().length > 0 ? (
            <List dense disablePadding>
              {getProcedures().map((procedure, index) => (
                  <React.Fragment key={procedure.id || index}>
                  <ListItem 
                    sx={{ 
                      py: 1, 
                      px: 1,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <MedicalServicesIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {procedure.name || procedure.procedureName || 'Unnamed Procedure'}
                          </Typography>
                            {procedure.type && (
                          <Tooltip title={`Type: ${procedure.type}`}>
                            <Chip 
                              label={procedure.type} 
                              size="small" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                bgcolor: getProcedureTypeColor(procedure.type).bgcolor,
                                color: getProcedureTypeColor(procedure.type).color,
                                border: `1px solid ${getProcedureTypeColor(procedure.type).borderColor}`,
                              }} 
                            />
                          </Tooltip>
                            )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                            {procedure.description || 'No description available'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < getProcedures().length - 1 && (
                    <Divider component="li" variant="inset" sx={{ opacity: 0.6 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No procedures found in this request.
                </Typography>
              </Box>
            )}
          </Collapse>

          {procedureRequest.comments && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Notes: {procedureRequest.comments}
              </Typography>
            </Box>
          )}
        </CardContent>
        
        <CardActions sx={{ p: 2, pt: 0 }}>
          {isAvailableStatus(procedureRequest.status) && (
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth
              onClick={(e) => { e.stopPropagation(); handleOpenClaimDialog(); }}
            >
              Claim Request
            </Button>
          )}
          
          {isClaimedStatus(procedureRequest.status) && onIssue && (
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth
              onClick={(e) => { e.stopPropagation(); handleOpenIssueDialog(); }}
            >
              Issue Procedure
            </Button>
          )}
          
          {isIssuedStatus(procedureRequest.status) && (
            <Button 
              variant="outlined" 
              color="secondary" 
              fullWidth
              disabled
              sx={{ 
                cursor: 'default',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              Procedure Issued
            </Button>
          )}
        </CardActions>
      </Card>
      
      {/* Claim Dialog */}
      <Dialog
        open={claimDialogOpen}
        onClose={handleCloseClaimDialog}
        aria-labelledby="claim-dialog-title"
        aria-describedby="claim-dialog-description"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle id="claim-dialog-title">
          Confirm Claim
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="claim-dialog-description">
            Are you sure you want to claim this procedure request for {procedureRequest.patientName}? 
            You will be responsible for reviewing and issuing {getProcedures().length} procedure(s).
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Procedures:</Typography>
            <List dense>
              {getProcedures().map((procedure) => (
                <ListItem key={procedure.id || procedure.procedureId} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={procedure.name || procedure.procedureName || 'Unnamed Procedure'}
                    secondary={procedure.description || 'No description available'}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClaimDialog} color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleClaim} color="primary" variant="contained" autoFocus disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Confirm Claim'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Issue Dialog */}
      <Dialog
        open={issueDialogOpen}
        onClose={handleCloseIssueDialog}
        aria-labelledby="issue-dialog-title"
        aria-describedby="issue-dialog-description"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle id="issue-dialog-title">
          Confirm Issue
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="issue-dialog-description">
            Are you sure you want to issue this procedure request for {procedureRequest.patientName}?
            This will open the detailed procedure view where you can review and finalize the procedures.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIssueDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleIssue} color="primary" variant="contained" autoFocus>
            Review & Issue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ProcedureRequestCard.propTypes = {
  procedureRequest: PropTypes.shape({
    requestId: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    userId: PropTypes.string.isRequired,
    patientInfo: PropTypes.shape({
      age: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      gender: PropTypes.string,
      bloodType: PropTypes.string,
      allergies: PropTypes.arrayOf(PropTypes.string),
      chronicConditions: PropTypes.arrayOf(PropTypes.string)
    }),
    procedures: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        procedureName: PropTypes.string,
        description: PropTypes.string,
        type: PropTypes.string
      })
    ),
    requestedAt: PropTypes.string,
    createdAt: PropTypes.string,
    status: PropTypes.string.isRequired,
    comments: PropTypes.string,
    assignedDoctorId: PropTypes.string,
    issuedAt: PropTypes.string
  }).isRequired,
  onSelect: PropTypes.func,
  onClaim: PropTypes.func,
  onIssue: PropTypes.func
};

ProcedureRequestCard.defaultProps = {
  onSelect: null,
  onClaim: null,
  onIssue: null
};

export default ProcedureRequestCard; 